// @flow
import React, {Fragment, useEffect, useState} from 'react';
import {BreakpointContext, XLARGE} from '@core-ui/breakpoint-provider';
import {customAttribute, Deferred} from '@core-ui/reporting';
import {useLocalize} from '@core-ui/localization-provider';
import {TAG_LINK_GROUP_LABEL} from './localization-tokens';
import universal from 'react-universal-component';
import {categoryPageEngagementDatalayer} from '../../datalayer/events/category-page-engagement';
import {facetEngagementDatalayerEvent} from '../../datalayer/events/facet-engagement';
import {getRelatedSubCategories} from '../../adapters/getRelatedSubCategories';
import {
  getCurrentCategory,
  getRelatedCategories,
} from '../../adapters/categoriesHandler';
import {shouldHideTitleText} from '../category-banner';
import RelatedCategories from '../related-categories';
import {defaultOptions} from '../../../helpers/universal';
import { modelToggleEnabled } from '@cat/size-model-toggle';

const isBrowser = typeof window !== 'undefined';

const FacetedGrid = isBrowser
  ? require('@core-ui/faceted-grid').default
  : () => <div className="faceted-grid"/>;

const LeftFacetedGrid = isBrowser
    ? universal(import('../left-faceted-grid'), defaultOptions)
    : () => <div className="faceted-grid"/>;

const CategoryBanner = universal(import('../category-banner'), defaultOptions);
const ProductBreadcrumbs = universal(
    import('@core-ui/product-breadcrumbs'),
    defaultOptions,
);
const LeftNav = universal(import('@core-ui/left-nav'), defaultOptions);
const LeftMobileBreadcrumbs = universal(
    import('../left-mobile-breadcrumbs'),
    defaultOptions,
);

const doesPageIncludeTopPrimaryBanner = (marketingData = {}) => {

  return marketingData &&
      marketingData.data &&
      marketingData.data.components &&
      marketingData.data.components.topPrimary;
};

type Props = {
  abbrBrand: string,
  abbrNameForTealium: string,
  brand: string,
  criticalResources: CriticalResources,
  enabledFeatures: { [key: string]: boolean },
  featureVariables: { [key: string]: any },
  errorLogger: () => void,
  initialSizeClass: string,
  locale: Locale,
  pageType: string,
  marketAgnosticBrandCode: number,
  translations: TranslationData,
  highPriorityEvents: Array<any>, // TODO
  params: { cid: string, style: any }, // TODO style not required?
  breadcrumbs: any, // TODO
  contentData: any, // TODO
  webHierarchy: any, // TODO
  selectedNodes: SelectedNodes,
  abSeg: any, // TODO
  market: any, // TODO
  categoryName: string,
  leftNav: LeftNavType,
  datalayer: DataLayer, // TODO
  criticalCss: any, // TODO
  brandName: any, // TODO
  businessUnitId: any, // TODO
  locationConfig: any, // TODO
  productsUrl: string,
  productsApikey: string,
  showPreviewBar: any, // TODO
  webHierarchy: any, // TODO
  smartCrosslinks: { crosslinks: Array<?{ [key: string]: string }>, globalVariables: { [key: string]: string } },
};

const CategoryPage = (props: Props) => {
  const {
    abbrBrand,
    abbrNameForTealium,
    abSeg,
    brandName,
    breadcrumbs,
    businessUnitId,
    categoryName,
    contentData,
    criticalCss,
    criticalResources,
    datalayer,
    enabledFeatures,
    errorLogger,
    featureVariables,
    highPriorityEvents,
    leftNav,
    locale,
    locationConfig,
    market,
    pageType,
    params: { cid, style },
    productsUrl,
    productsApikey,
    selectedNodes,
    showPreviewBar,
    webHierarchy,
    smartCrosslinks,
  } = props;

  const [isSubCategoryPage, setIsSubCategoryPage] = useState(false);
  const [relatedSubCategoryLinks, setRelatedSubCategoryLinks] = useState([]);

  useEffect(() => {
    categoryPageEngagementDatalayer(props);
  }, [props]);

  useEffect(() => {
    highPriorityEvents.push({
      name: 'cat1ImageLoaded',
      deferred: new Deferred(),
    });
  }, [highPriorityEvents]);

  const onFacetChange = facets => {
    const isSubCat = !!(facets && facets.appliedFacets &&
        facets.appliedFacets.style
        && facets.appliedFacets.style.length > 0);
    setIsSubCategoryPage(isSubCat);
    setRelatedSubCategoryLinks(
        isSubCat ? getRelatedSubCategories(facets, cid) : []);
  };

  const {url: _, ...categoryWithoutUrl} = breadcrumbs?.category ?? {};
  const breadcrumbsWithoutCategoryUrl = {
    ...breadcrumbs,
    category: categoryWithoutUrl,
  };
  const divisionMarketingData = contentData && contentData.division;
  const CATEGORY_BANNERS = 'category-banners';
  const LEFT_NAV_BANNERS = 'left-nav-banners';
  const {localize} = useLocalize();
  const currentCategory = getCurrentCategory({webHierarchy, selectedNodes});
  const relatedCategoryLinks = getRelatedCategories(
    {webHierarchy, selectedNodes});

  const railFacetEnabled = () => (abSeg && abSeg[`${abbrBrand}02`] === 'a'
    && enabledFeatures[`left-rail-facet-${abbrBrand}-${market}`]);
  const railMobileFacetEnabled = () => (abSeg && abSeg[`${abbrBrand}01`] ===
    'a'
    && enabledFeatures[`left-rail-mobile-facet-${abbrBrand}-${market}`]);

  const isModelToggleEnabled = modelToggleEnabled({
    featureVariables,
    enabledFeatures,
    abSeg,
    market,
    abbrBrand,
    cid,
  });

  let relatedLinks = isSubCategoryPage
    ? relatedSubCategoryLinks
    : relatedCategoryLinks;
  let smartCrosslinkSource = relatedLinks?.length > 0
    ? 'webHierarchy (legacy)'
    : 'noData (componentFailure)';
  let customLabel = '';

  if (smartCrosslinks?.crosslinks?.length > 0) {
    customLabel = localize(TAG_LINK_GROUP_LABEL);
    relatedLinks = smartCrosslinks.crosslinks;
    smartCrosslinkSource = 'bloomReach';
  }

  customAttribute('smartCrosslinkSource', smartCrosslinkSource);

  const categoryBreadcrumbEnabled = enabledFeatures[`cat-breadcrumb-${abbrBrand}`];
  const productGridClassNames = (railFacetEnabled() ? 'product-grid product-grid--with-left-hand-facets' : 'product-grid');

  const categoryContentData = contentData && contentData.category && contentData.category[cid];

  return (
    <Fragment>
      { doesPageIncludeTopPrimaryBanner(categoryContentData) && (
        <div className="category-banner category-banner--top-primary">
          <CategoryBanner
            categoryName={categoryName}
            criticalCss={criticalCss}
            divisionMarketingData={divisionMarketingData}
            hasHiddenTitle={shouldHideTitleText(
              railMobileFacetEnabled(),
              breadcrumbs
            )}
            mktingType={CATEGORY_BANNERS}
            position="topPrimary"
            selectedNodes={selectedNodes}
          />
        </div>
      )}
      <div className="category-banner" id="main-content">
        <CategoryBanner
          categoryName={categoryName}
          criticalCss={criticalCss}
          divisionMarketingData={divisionMarketingData}
          hasHiddenTitle={shouldHideTitleText(
            railMobileFacetEnabled(),
            breadcrumbs
          )}
          mktingType={CATEGORY_BANNERS}
          position="top"
          selectedNodes={selectedNodes}
        />
      </div>

      {categoryBreadcrumbEnabled && ( // render the breadcrumbs with a feature flag
        <div className="category-breadcrumbs">
          <BreakpointContext.Consumer>
            {({ smallerThan }) => {
              if (smallerThan(XLARGE) && railMobileFacetEnabled()) {
                return (
                  <LeftMobileBreadcrumbs
                    breadcrumbs={breadcrumbsWithoutCategoryUrl}
                  />
                );
              }
              return (
                <ProductBreadcrumbs
                  breadcrumbs={breadcrumbsWithoutCategoryUrl}
                />
              );
            }}
          </BreakpointContext.Consumer>
        </div>
      )}
      <div className={`sds_grid-root ${productGridClassNames}`}>
        <BreakpointContext.Consumer>
          {
            ({ greaterOrEqualTo, smallerThan }) => {
              if ((greaterOrEqualTo(XLARGE) && railFacetEnabled())
                || (smallerThan(XLARGE) && railMobileFacetEnabled())) {
                return (
                  <LeftFacetedGrid
                    {...props}
                    productsUrl={productsUrl}
                    productsApikey={productsApikey}
                    currentCategory={currentCategory}
                    isDesktop={greaterOrEqualTo(XLARGE)}
                    onFacetChange={onFacetChange}
                    isModelToggleEnabled={isModelToggleEnabled}
                  />
                );
              }
              return (
                <Fragment>
                  <LeftNav
                    divisionMarketingData={divisionMarketingData}
                    leftNav={leftNav}
                    mktingType={LEFT_NAV_BANNERS}
                    selectedNodes={selectedNodes}
                  />
                  <FacetedGrid
                    abbrBrand={abbrBrand}
                    abbrNameForTealium={abbrNameForTealium}
                    abSeg={abSeg}
                    brandName={brandName}
                    businessUnitId={businessUnitId}
                    categoryName={categoryName}
                    cid={cid}
                    isModelToggleEnabled={isModelToggleEnabled}
                    criticalCss={criticalCss}
                    criticalResources={criticalResources}
                    currentCategory={currentCategory}
                    datalayer={datalayer}
                    errorLogger={errorLogger}
                    facetDataLayerBuilder={facetEngagementDatalayerEvent}
                    featureVariables={featureVariables}
                    highPriorityEvents={highPriorityEvents}
                    locale={locale}
                    locationConfig={locationConfig}
                    market={market}
                    onFacetChange={onFacetChange}
                    pageType={pageType}
                    productsUrl={productsUrl}
                    productsApikey={productsApikey}
                    selectedNodes={selectedNodes}
                    showPreviewBar={showPreviewBar}
                    style={style}
                  />
                </Fragment>
              );
            }
          }
        </BreakpointContext.Consumer>
      </div>
      <RelatedCategories customLabel={customLabel} categories={relatedLinks} />
      <div className="category-banner">
        <CategoryBanner
          categoryName={categoryName}
          criticalCss={criticalCss}
          divisionMarketingData={divisionMarketingData}
          mktingType={CATEGORY_BANNERS}
          position="bottom"
          selectedNodes={selectedNodes}
        />
      </div>
      <div className="cat-modal" />
    </Fragment>
  );
};

CategoryPage.defaultProps = {
  highPriorityEvents: [],
};

export default CategoryPage;
